import React, { useEffect, useState } from 'react';
import { ProjectInfoPanel } from '../../components/NewProjectModal';
import { updatePatient, updatePatientPhoto, type PatientRecord, type PatientUpdateInput } from '../../services/patientService';
import { toApiDate } from '../../utils/dateFormat';

type Props = {
  patient: { name: string; dob: string; gender?: string; patientId?: string; hasPhoto?: boolean; photoVersion?: string };
  token?: string | null;
  onSaved: (patient: PatientRecord) => void;
  onEditingChange: (editing: boolean) => void;
  loading?: boolean;
  loadError?: string | null;
  onRetry: () => void;
};

export default function ViewerPatientInfo({
  patient,
  token,
  onSaved,
  onEditingChange,
  loading,
  loadError,
  onRetry,
}: Props) {
  const [draft, setDraft] = useState<typeof patient | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const value = draft ?? patient;
  const canEdit = Boolean(patient.patientId && token && !loading && !loadError);
  const demographicsChanged = Boolean(
    draft &&
      (draft.name !== patient.name ||
        draft.dob !== patient.dob ||
        (draft.gender || '') !== (patient.gender || ''))
  );
  const isDirty = demographicsChanged || Boolean(photo);
  useEffect(() => {
    // Keep the popup open only while it contains unsaved changes.
    onEditingChange(isDirty || saving);
  }, [isDirty, saving, onEditingChange]);

  const change = (field: 'name' | 'dob' | 'gender', next: string) => {
    if (!canEdit || saving) return;
    setDraft(current => {
      const changed = { ...(current ?? patient), [field]: next };
      return changed.name === patient.name &&
        changed.dob === patient.dob &&
        (changed.gender || '') === (patient.gender || '')
        ? null
        : changed;
    });
    setSaved(false);
    setError('');
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isDirty || !canEdit || saving) return;
    const birthDate = toApiDate(value.dob);
    if (demographicsChanged && (!value.name.trim() || value.name.trim().length > 255)) {
      setError('Enter a name between 1 and 255 characters.');
      return;
    }
    // toApiDate leaves invalid calendar dates unchanged.
    if (demographicsChanged && (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate) || new Date(`${birthDate}T00:00:00`) > new Date())) {
      setError('Enter a valid date of birth that is not in the future.');
      return;
    }
    if (demographicsChanged && !value.gender?.trim()) {
      setError('Select a gender.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const demographics: PatientUpdateInput | undefined = demographicsChanged ? {
          patient_name: value.name.trim(),
          birth_date: birthDate,
          gender: value.gender!,
        } : undefined;
      // Photo and demographic changes are committed together by the photo
      // endpoint, so a failed upload cannot leave a partially saved form.
      const updated = photo
        ? await updatePatientPhoto(patient.patientId, photo, token, demographics)
        : await updatePatient(patient.patientId, demographics!, token);
      onSaved(updated);
      setDraft(null);
      setPhoto(null);
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Patient information could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      aria-label="Patient information form"
      noValidate
    >
      <ProjectInfoPanel
        fullName={value.name}
        dob={value.dob}
        gender={value.gender || ''}
        isNameAutofilled={false}
        isDobAutofilled={false}
        isGenderAutofilled={false}
        patientId={patient.patientId}
        hasStoredPhoto={patient.hasPhoto}
        photoVersion={patient.photoVersion}
        patientPhoto={photo}
        onPatientPhotoChange={next => {
          if (!canEdit || saving) return;
          setPhoto(next);
          setSaved(false);
          setError('');
        }}
        token={token}
        readOnly={!canEdit || saving}
        photoReadOnly={!canEdit || saving}
        showHeading={false}
        onFullNameChange={next => change('name', next)}
        onDobChange={next => change('dob', next)}
        onGenderChange={next => change('gender', next)}
      />
      {(error || loadError) && (
        <p
          className="pc-patient-info-error"
          role="alert"
        >
          {error || loadError}
        </p>
      )}
      {saved && (
        <p
          className="pc-patient-info-status"
          role="status"
        >
          Patient information saved.
        </p>
      )}
      {loading && (
        <p
          className="pc-patient-info-status"
          role="status"
        >
          Loading patient information…
        </p>
      )}
      {(isDirty || loadError) && (
        <div className="pc-patient-info-actions">
          {isDirty && (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setDraft(null);
                  setPhoto(null);
                  setError('');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canEdit || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          {loadError && (
            <button
              type="button"
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </form>
  );
}
